import React, { useMemo, useState } from "react";

import { getByPath } from "../lib/ioSchema.js";
import ConfidenceIndicator from "./ConfidenceIndicator.jsx";

const TIERS = [
  { key: "all", label: "All confidence" },
  { key: "high", label: "High (≥ 0.90)", test: (s) => s >= 0.9 },
  { key: "medium", label: "Medium (0.70–0.89)", test: (s) => s >= 0.7 && s < 0.9 },
  { key: "low", label: "Low (< 0.70)", test: (s) => s < 0.7 },
];

// Confidence keys use bracket notation ("line_items[0].rate", the wildcarded
// aggregate "line_items[*].rate"); getByPath expects dotted+indexed paths
// ("line_items.0.rate"). Wildcard paths have no single backing value.
function resolveValue(ioRecord, bracketPath) {
  if (bracketPath.includes("[*]")) return null;
  const dotPath = bracketPath.replace(/\[(\d+)\]/g, ".$1");
  const value = getByPath(ioRecord, dotPath);
  if (value == null) return null;
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * ConfidenceReport — sortable, filterable table of every field confidence
 * score the LLM produced for the extracted IO(s), so a trafficker can see
 * exactly what was parsed and how sure the model was, without digging
 * through the raw JSON.
 */
export default function ConfidenceReport({ ioOrders }) {
  const [search, setSearch] = useState("");
  const [tier, setTier] = useState("all");
  const [sortKey, setSortKey] = useState("score");
  const [sortDir, setSortDir] = useState("asc");

  const multiOrder = (ioOrders || []).length > 1;

  const rows = useMemo(() => {
    const out = [];
    for (const io of ioOrders || []) {
      const conf = io?._meta?.confidence;
      if (!conf || typeof conf !== "object") continue;
      for (const [field, score] of Object.entries(conf)) {
        if (typeof score !== "number") continue;
        out.push({ order: io.io_number || "—", field, score, value: resolveValue(io, field) });
      }
    }
    return out;
  }, [ioOrders]);

  const filtered = useMemo(() => {
    let out = rows;
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((r) => r.field.toLowerCase().includes(q) || r.order.toLowerCase().includes(q));
    }
    const tierDef = TIERS.find((t) => t.key === tier);
    if (tierDef?.test) out = out.filter((r) => tierDef.test(r.score));

    out = [...out].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === "string" && typeof bv === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return out;
  }, [rows, search, tier, sortKey, sortDir]);

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "score" ? "asc" : "asc");
    }
  }

  if (!ioOrders || ioOrders.length === 0) return null;

  const lowCount = rows.filter((r) => r.score < 0.7).length;
  const mediumCount = rows.filter((r) => r.score >= 0.7 && r.score < 0.9).length;

  return (
    <div className="confidence-report">
      <div className="confidence-report-header">
        <h3>
          Field Confidence
          <span className="order-count-badge">{rows.length} field{rows.length === 1 ? "" : "s"}</span>
        </h3>
        <div className="confidence-report-controls">
          <input
            type="text"
            placeholder="Filter by field or order…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={tier} onChange={(e) => setTier(e.target.value)}>
            {TIERS.map((t) => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>

      {(lowCount > 0 || mediumCount > 0) && (
        <div className="alert alert-warning">
          {lowCount > 0 && <span>{lowCount} field{lowCount === 1 ? "" : "s"} low confidence — needs review. </span>}
          {mediumCount > 0 && <span>{mediumCount} field{mediumCount === 1 ? "" : "s"} medium confidence.</span>}
        </div>
      )}

      <div className="confidence-table-wrap">
        <table className="confidence-table">
          <thead>
            <tr>
              {multiOrder && <th className="sortable" onClick={() => handleSort("order")}>Order</th>}
              <th className="sortable" onClick={() => handleSort("field")}>Field</th>
              <th>Parsed Value</th>
              <th className="sortable num" onClick={() => handleSort("score")}>Confidence</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={multiOrder ? 4 : 3} className="table-empty">No fields match the current filter.</td>
              </tr>
            )}
            {filtered.map((r) => (
              <tr key={`${r.order}:${r.field}`}>
                {multiOrder && <td className="mono">{r.order}</td>}
                <td className="mono">{r.field}</td>
                <td className="confidence-report-value">{r.value ?? "—"}</td>
                <td className="num">
                  <span className="confidence-cell">
                    <ConfidenceIndicator confidence={r.score} />
                    <span className="mono">{r.score.toFixed(2)}</span>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
